import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ImportReview } from 'components/questionBank/ImportReview';
import { useSubjectList } from 'modules/subject';
import type { Subject } from 'modules/subject/types';
import { useImportReviewStore } from 'store/importReview.store';

const QUESTION_BANK_PATH = '/app/questions';

/**
 * Review step of a file import. The parsed batch arrives through
 * `useImportReviewStore`; with none there (a reload, a pasted link) there is
 * nothing to review, so it sends the user back to upload again.
 */
export const ImportReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: subjects = [] } = useSubjectList();
  const preview = useImportReviewStore((state) => state.preview);
  const defaultSubjectId = useImportReviewStore((state) => state.defaultSubjectId);
  const clear = useImportReviewStore((state) => state.clear);

  // The commit can finish after the user has navigated elsewhere; the batch is
  // still cleared (it is saved — reopening it would offer a duplicate import),
  // but they are not pulled back.
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  if (!preview) return <Navigate to={QUESTION_BANK_PATH} replace />;

  // Cleared here rather than in an unmount effect: StrictMode's simulated
  // unmount would wipe the batch the page has only just opened with. A batch
  // left behind by navigating away is replaced by the next import.
  const leave = () => {
    clear();
    if (mounted.current) navigate(QUESTION_BANK_PATH);
  };

  return (
    <ImportReview
      preview={preview}
      subjects={subjects as Subject[]}
      defaultSubjectId={defaultSubjectId}
      onBack={leave}
      onDone={leave}
    />
  );
};
